import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import TextInput from '../../../shared/Inputs/TextInput/TextInput'
import SelectInput from '../../../shared/Inputs/SelectInput/SelectInput'
import DatePicker from '../../../shared/Inputs/DatePicker/DatePicker'
import {
  validateString,
  smartValidateString,
} from '../../../validate/validators'
import { Country, Lang } from '../../../types/common-types'
import styles from '../ScreenOne.module.scss'
import { State } from '../../../state-actions-reducers'
import { INSTANCE } from '../../../server-parameters'
import TroubleMessage from '../../../shared/TroubleMessage/TroubleMessage'
import { copyFromRegistered } from '../../../utils/helpers'

interface Props {
  lang: Lang
  accountForm: any
  addressCountries: Country[]
  regionCityRegistered: any
  regionCityFactual: any
}

export default function AddressInputs({
  lang,
  accountForm,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
}: Props) {
  const { watch, setValue, getValues, formState: { errors } } = useFormContext<State>()
  const state = watch()

  const priorityRegions = [
    ['Москва и Московская обл.', 'Мәскеу және Мәскеу облысы', 'Moscow and Moscow region'],
    ['Санкт-Петербург и область', 'Санкт-Петербург және облыс', 'St.Petersburg and Leningrad region'],
    ['Алматинская область', 'Алматы облысы', 'Almaty']
  ];

  const getPriorityRegions = (instance: string) => {
    if (instance === 'msk') {
      return [
        priorityRegions[0], // Москва
        priorityRegions[1], // СПб
        priorityRegions[2]  // Алматы
      ];
    } else if (instance === 'spb' || instance === 'second') {
      return [
        priorityRegions[1], // СПб
        priorityRegions[0], // Москва
        priorityRegions[2]  // Алматы
      ];
    }
    return priorityRegions; // по умолчанию оригинальный порядок
  };

  const regionRegisteredListUnigue = [
    ...new Set(regionCityRegistered.map((item: any) => item.region))
  ].sort((a, b) => {
    const currentPriorityRegions = getPriorityRegions(INSTANCE);

    for (const regionGroup of currentPriorityRegions) {
      const aInGroup = regionGroup.includes(a as string);
      const bInGroup = regionGroup.includes(b as string);
      if (aInGroup && !bInGroup) return -1;
      if (!aInGroup && bInGroup) return 1;
      if (aInGroup && bInGroup) break;
    }

    return (a as string).localeCompare(b as string);
  });
  const cityRegisteredListForRegion = regionCityRegistered.filter(
    (item: any) => {
      if (item.region === state.addressRegisteredRegion) return item
    }
  )
  const regionRegisteredList = regionRegisteredListUnigue.map((item: any) => {
    return {
      value: item,
      label: {
        rus: item,
        eng: item,
        kz: item,
      },
    }
  }).filter((e) => (e.value !== 'Республика Крым и г. Севастополь' && INSTANCE === 'ala') || INSTANCE !== 'ala')
  const cityRegisteredList = cityRegisteredListForRegion
    ? cityRegisteredListForRegion
      .map((item: any) => {
        return {
          value: item.city,
          label: {
            rus: item.city,
            eng: item.city,
            kz: item.city,
          },
        }
      })
      .sort(
        (
          a: { value: string; label: string },
          b: { value: string; label: string }
        ) => {
          if (a.value.toLowerCase() < b.value.toLowerCase()) {
            return -1
          }
          if (a.value.toLowerCase() > b.value.toLowerCase()) {
            return 1
          }
          return 0
        }
      ).sort((x: any, y: any) =>
        ['Москва', 'Мәскеу', 'Moscow'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Санкт-Петербург', 'Санкт-Петербург', 'Saint-Petersburg'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Алматы', 'Алматы', 'Almaty'].includes(x.value)
          ? -1
          : 1
      )
    : []

  // Factual List

  const regionFactualListUnigue = [
    ...new Set(regionCityFactual.map((item: any) => item.region))
  ].sort((a, b) => {
    const currentPriorityRegions = getPriorityRegions(INSTANCE);

    for (const regionGroup of currentPriorityRegions) {
      const aInGroup = regionGroup.includes(a as string);
      const bInGroup = regionGroup.includes(b as string);
      if (aInGroup && !bInGroup) return -1;
      if (!aInGroup && bInGroup) return 1;
      if (aInGroup && bInGroup) break;
    }

    return (a as string).localeCompare(b as string);
  });
  const cityFactualListForRegion = regionCityFactual.filter((item: any) => {
    if (item.region === state.addressFactualRegion) return item
  })
  const regionFactualList = regionFactualListUnigue.map((item: any) => {
    return {
      value: item,
      label: {
        rus: item,
        eng: item,
        kz: item,
      },
    }
  }).filter((e) => (e.value !== 'Республика Крым и г. Севастополь' && INSTANCE === 'ala') || INSTANCE !== 'ala')
  const cityFactualList = cityFactualListForRegion
    ? cityFactualListForRegion
      .map((item: any) => {
        return {
          value: item.city,
          label: {
            rus: item.city,
            eng: item.city,
            kz: item.city,
          },
        }
      })
      .sort(
        (
          a: { value: string; label: string },
          b: { value: string; label: string }
        ) => {
          if (a.value.toLowerCase() < b.value.toLowerCase()) {
            return -1
          }
          if (a.value.toLowerCase() > b.value.toLowerCase()) {
            return 1
          }
          return 0
        }
      ).sort((x: any, y: any) =>
        ['Москва', 'Мәскеу', 'Moscow'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Санкт-Петербург', 'Санкт-Петербург', 'Saint-Petersburg'].includes(x.value)
          ? -1
          : 1
      ).sort((x: any, y: any) =>
        ['Алматы', 'Алматы', 'Almaty'].includes(x.value)
          ? -1
          : 1
      )
    : []
  const [disabledAddress, setDisabledAddress] = useState(false)
  useEffect(() => {
    if (state.addressFactualCopyFromRegistered) setDisabledAddress(true)
    if (
      state.addressRegisteredRegion.length > 0 &&
      !regionRegisteredList.find(item => {
        return item.value === state.addressRegisteredRegion
      })
    ) {
      setValue('addressRegisteredRegion', '', { shouldDirty: true })
      setValue('addressRegisteredCity', '', { shouldDirty: true })
    }
    if (
      state.addressFactualRegion.length > 0 &&
      !regionFactualList.find(item => {
        return item.value === state.addressFactualRegion
      })
    ) {
      setValue('addressRegisteredRegion', '', { shouldDirty: true })
      setValue('addressRegisteredCity', '', { shouldDirty: true })
    }
  }, [])
  console.log('state.addressFactualRegion', state.addressFactualRegion)
  return (
    <>
      {/* REGISTRATION ADDRESS */}
      <div className="form-group">
        <div className="row">
          <div className="col-12 col-xs-12">
            <h3>{accountForm.stepAddressRegistered[lang]}</h3>
          </div>
          <div className="col-4 col-xs-12">
            {/* REGISTRATION ADDRESS COUNTRY */}
            <SelectInput
              testName={'addressRegisteredCountry'}
              testErrorName={'error-addressRegisteredCountry'}
              noOptionsMessage={accountForm.citizenshipInput.placeholder}
              lang={lang}
              disabled={!state.agreed}
              content={accountForm.addressCountry}
              options={addressCountries}
              onChange={id => {
                setValue('addressRegisteredCountry',
                  addressCountries[
                    addressCountries.findIndex(item => item.value === id)
                  ].value,
                  { shouldDirty: true }
                )
                setValue('addressRegisteredRegion', '', { shouldDirty: true })
                setValue('addressRegisteredCity', '', { shouldDirty: true })
                if (disabledAddress) {
                  setValue('addressFactualCountry',
                    addressCountries[
                      addressCountries.findIndex(item => item.value === id)
                    ].value,
                    { shouldDirty: true }
                  )
                }
              }}
              valid={
                !errors.addressRegisteredCountry &&
                validateString(state.addressRegisteredCountry, 300)
              }
              value={state.addressRegisteredCountry}
              prefix={
                state.iframeCountry === 'kz'
                  ? 'RegisteredAddressCountry'
                  : 'RegisteredAddressCountryRf'
              }
            />
          </div>
          <div className="col-4 col-xs-12">
            {/* REGISTRATION ADDRESS REGION */}
            {regionRegisteredList.length > 0 ? (
              <SelectInput
                testName={'addressRegisteredRegion'}
                testErrorName={'error-addressRegisteredRegion'}
                lang={lang}
                noOptionsMessage={accountForm.addressRegion.placeholder}
                content={accountForm.addressRegion}
                value={state.addressRegisteredRegion}
                options={regionRegisteredList}
                valid={
                  state.addressRegisteredRegion.length > 0
                    ? smartValidateString(state.addressRegisteredRegion) &&
                    !errors.addressRegisteredRegion
                    : !errors.addressRegisteredRegion
                }
                onChange={addressRegisteredRegion => {
                  setValue('addressRegisteredRegion', addressRegisteredRegion, { shouldDirty: true })
                  setValue('addressRegisteredCity', '', { shouldDirty: true })
                  if (disabledAddress) {
                    setValue('addressFactualRegion', addressRegisteredRegion, { shouldDirty: true })
                  }
                }}
                className={styles.input}
                prefix={
                  state.addressRegisteredCountry === '398'
                    ? 'RegisteredAddressRegion'
                    : state.addressRegisteredCountry === '643'
                      ? 'RegisteredAddressRegionRf'
                      : undefined
                }
              />
            ) : (
              <TextInput
                testName="addressRegisteredRegion"
                testErrorName="error-addressRegisteredRegion"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.addressRegion}
                onChange={addressRegisteredRegion => {
                  setValue('addressRegisteredRegion', addressRegisteredRegion, { shouldDirty: true })
                  if (disabledAddress) {
                    setValue('addressFactualRegion', addressRegisteredRegion, { shouldDirty: true })
                  }
                }}
                valid={
                  state.addressRegisteredRegion.length > 0
                    ? smartValidateString(state.addressRegisteredRegion) &&
                    !errors.addressRegisteredRegion
                    : !errors.addressRegisteredRegion
                }
                value={state.addressRegisteredRegion}
              />
            )}
          </div>
          <div className="col-4 col-xs-12">
            {/* REGISTRATION ADDRESS CITY */}
            {regionRegisteredList.length > 0 ? (
              <SelectInput
                testName={'addressRegisteredCity'}
                testErrorName={'error-addressRegisteredCity'}
                disabled={!state.addressRegisteredRegion}
                lang={lang}
                noOptionsMessage={accountForm.addressCity.placeholder}
                content={accountForm.addressCity}
                value={state.addressRegisteredCity}
                optionValue={{
                  value: state.addressRegisteredCity,
                  label: state.addressRegisteredCity
                }}
                options={cityRegisteredList}
                valid={
                  state.addressRegisteredCity.length > 0
                    ? smartValidateString(state.addressRegisteredCity) &&
                    !errors.addressRegisteredCity
                    : !errors.addressRegisteredCity
                }
                onChange={addressRegisteredCity => {
                  setValue('addressRegisteredCity', addressRegisteredCity, { shouldDirty: true })
                  if (disabledAddress) {
                    setValue('addressFactualCity', addressRegisteredCity, { shouldDirty: true })
                  }
                }}
                onCreateOption={
                  (addressRegisteredCity: any) => {
                    setValue('addressRegisteredCity', addressRegisteredCity, { shouldDirty: true })
                    if (disabledAddress) {
                      setValue('addressFactualCity', addressRegisteredCity, { shouldDirty: true })
                    }
                  }
                }
                className={styles.input}
                prefix={
                  state.addressRegisteredCountry === '398' && ['Алматинская область', 'Алматы облысы', 'Almaty'].includes(state.addressRegisteredRegion)
                    ? 'RegisteredAddressCity'
                    : state.addressRegisteredCountry === '643' && ['Москва и Московская обл.', 'Мәскеу және Мәскеу облысы', 'Moscow and Moscow region', 'Санкт-Петербург и область', 'Санкт-Петербург және облыс', 'St.Petersburg and Leningrad region'].includes(state.addressRegisteredRegion)
                      ? 'RegisteredAddressCityRf'
                      : undefined
                }
                isSearchable={true}
                creatable={true}
              />
            ) : (
              <TextInput
                testName="addressRegisteredCity"
                testErrorName="error-addressRegisteredCity"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.addressCity}
                onChange={addressRegisteredCity => {
                  setValue('addressRegisteredCity', addressRegisteredCity, { shouldDirty: true })
                  if (disabledAddress) {
                    setValue('addressFactualCity', addressRegisteredCity, { shouldDirty: true })
                  }
                }}
                valid={
                  state.addressRegisteredCity.length > 0
                    ? smartValidateString(state.addressRegisteredCity) &&
                    !errors.addressRegisteredCity
                    : !errors.addressRegisteredCity
                }
                value={state.addressRegisteredCity}
              />
            )}
          </div>
          <div className="col-4 col-xs-12">
            {/* REGISTRATION ADDRESS STREET */}
            <TextInput
              testName="addressRegisteredStreet"
              testErrorName="error-addressRegisteredStreet"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressStreet}
              onChange={addressRegisteredStreet => {
                setValue('addressRegisteredStreet', addressRegisteredStreet, { shouldDirty: true })
                if (disabledAddress) {
                  setValue('addressFactualStreet', addressRegisteredStreet, { shouldDirty: true })
                }
              }}
              valid={
                state.addressRegisteredStreet.length > 0
                  ? smartValidateString(state.addressRegisteredStreet) &&
                  !errors.addressRegisteredStreet
                  : !errors.addressRegisteredStreet
              }
              value={state.addressRegisteredStreet}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* REGISTRATION ADDRESS BUILDING */}
            <TextInput
              testName="addressRegisteredBuilding"
              testErrorName="error-addressRegisteredBuilding"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressBuilding}
              onChange={addressRegisteredBuilding => {
                setValue('addressRegisteredBuilding', addressRegisteredBuilding, { shouldDirty: true })
                if (disabledAddress) {
                  setValue('addressFactualBuilding', addressRegisteredBuilding, { shouldDirty: true })
                }
              }}
              valid={
                validateString(state.addressRegisteredBuilding, 100) &&
                !errors.addressRegisteredBuilding
              }
              value={state.addressRegisteredBuilding}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* REGISTRATION ADDRESS BUILDING BLOCK */}
            <TextInput
              testName="addressRegisteredBuildingBlock"
              testErrorName="error-addressRegisteredBuildingBlock"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressBuildingBlock}
              onChange={addressRegisteredBuildingBlock => {
                setValue('addressRegisteredBuildingBlock', addressRegisteredBuildingBlock, { shouldDirty: true })
                if (disabledAddress) {
                  setValue('addressFactualBuildingBlock', addressRegisteredBuildingBlock, { shouldDirty: true })
                }
              }}
              valid={
                validateString(state.addressRegisteredBuildingBlock, 100) &&
                !errors.addressRegisteredBuildingBlock
              }
              value={state.addressRegisteredBuildingBlock}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* REGISTRATION ADDRESS APARTMENTS */}
            <TextInput
              testName="addressRegisteredApartments"
              testErrorName="error-addressRegisteredApartments"
              disabled={!state.agreed}
              lang={lang}
              content={accountForm.addressApartments}
              onChange={addressRegisteredApartments => {
                setValue('addressRegisteredApartments', addressRegisteredApartments, { shouldDirty: true })
                if (disabledAddress) {
                  setValue('addressFactualApartments', addressRegisteredApartments, { shouldDirty: true })
                }
              }}
              valid={
                validateString(state.addressRegisteredApartments, 50) &&
                !errors.addressRegisteredApartments
              }
              value={state.addressRegisteredApartments}
            />
          </div>
          <div className="col-2 col-xs-12"></div>
          <div className="col-2 col-xs-12">
            <DatePicker
              testName="addressRegisteredDate"
              testErrorName="error-addressRegisteredDate"
              lang={lang}
              accountForm={accountForm}
              accountFormKey={
                state.citizenship === '398'
                  ? 'addressRegisteredDateNoValidate'
                  : 'addressRegisteredDate'
              }
              onChange={data => {
                setValue('addressRegisteredDate', data, { shouldDirty: true })
              }}
              value={state.addressRegisteredDate}
              valid={!errors.addressRegisteredDate}
              curr={new Date()}
              min={
                state.birthDate
                  ? new Date(state.birthDate)
                  : new Date(
                    new Date().setFullYear(new Date().getFullYear() - 85)
                  )
              }
              max={new Date()}
            />
          </div>
          <div className="col-12 col-xs-12">
            <TroubleMessage lang={lang} accountForm={accountForm} />
          </div>
        </div>
      </div>
      {/* FACTUAL ADDRESS */}
      <div className="form-group">
        <div className="row">
          <div className="col-12 col-xs-12"></div>
          <div className="col-12 col-xs-12">
            <h3>{accountForm.stepAddressFactual[lang]}</h3>
          </div>
          <div className={styles.checkInputBox + ' ' + 'col-12 col-xs-12'}>
            <input
              data-test-id="addressFactualCopyFromRegistered"
              id="copyInput"
              name="copyInput"
              type="checkBox"
              onChange={e => {
                const current = getValues()
                const next = copyFromRegistered(current as State)
                Object.keys(next).forEach(key => {
                  if ((next as any)[key] !== (current as any)[key]) {
                    setValue(key as any, (next as any)[key], { shouldDirty: true })
                  }
                })
                setDisabledAddress(e.target.checked)
              }}
              checked={state.addressFactualCopyFromRegistered}
            />
            <label htmlFor="copyInput">
              {accountForm.addressFactualCopyBtnDesc[lang]}
            </label>
          </div>
          <div className="col-4 col-xs-12">
            {/* FACTUAL ADDRESS COUNTRY */}
            <SelectInput
              testName={'addressFactualCountry'}
              testErrorName={'error-addressFactualCountry'}
              noOptionsMessage={accountForm.citizenshipInput.placeholder}
              lang={lang}
              disabled={!state.agreed || disabledAddress}
              content={accountForm.addressCountry}
              options={addressCountries}
              onChange={id => {
                setValue('addressFactualCountry',
                  addressCountries[
                    addressCountries.findIndex(item => item.value === id)
                  ].value,
                  { shouldDirty: true }
                )
                setValue('addressFactualRegion', '', { shouldDirty: true })
                setValue('addressFactualCity', '', { shouldDirty: true })
              }}
              valid={
                !errors.addressFactualCountry &&
                validateString(state.addressFactualCountry, 3, false)
              }
              value={state.addressFactualCountry}
              prefix={
                state.iframeCountry === 'kz'
                  ? 'FactualAddressCountry'
                  : 'FactualAddressCountryRf'
              }
            />
          </div>
          <div className="col-4 col-xs-12">
            {/* FACTUAL ADDRESS REGION */}
            {regionFactualList.length > 0 ? (
              <SelectInput
                testName={'addressFactualRegion'}
                testErrorName={'error-addressFactualRegion'}
                disabled={disabledAddress}
                className={styles.input}
                lang={lang}
                noOptionsMessage={accountForm.addressRegion.placeholder}
                content={accountForm.addressRegion}
                value={state.addressFactualRegion}
                options={regionFactualList}
                valid={
                  state.addressFactualRegion.length > 0
                    ? smartValidateString(state.addressFactualRegion) &&
                    !errors.addressFactualRegion
                    : !errors.addressFactualRegion
                }
                onChange={addressFactualRegion => {
                  setValue('addressFactualRegion', addressFactualRegion, { shouldDirty: true })
                  setValue('addressFactualCity', '', { shouldDirty: true })
                }}
                prefix={
                  state.addressFactualCountry === '398'
                    ? 'FactualAddressRegion'
                    : state.addressFactualCountry === '643'
                      ? 'FactualAddressRegionRf'
                      : undefined
                }
              />
            ) : (
              <TextInput
                testName="addressFactualRegion"
                testErrorName="error-addressFactualRegion"
                disabled={!state.agreed || disabledAddress}
                lang={lang}
                content={accountForm.addressRegion}
                onChange={addressFactualRegion => {
                  setValue('addressFactualRegion', addressFactualRegion, { shouldDirty: true })
                }}
                valid={
                  state.addressFactualRegion.length > 0
                    ? smartValidateString(state.addressFactualRegion) &&
                    !errors.addressFactualRegion
                    : !errors.addressFactualRegion
                }
                value={state.addressFactualRegion}
              />
            )}
          </div>
          <div className="col-4 col-xs-12">
            {/* FACTUAL ADDRESS CITY */}
            {regionFactualList.length > 0 ? (
              <SelectInput
                testName={'addressFactualCity'}
                testErrorName={'error-addressFactualCity'}
                className={styles.input}
                disabled={!state.addressFactualRegion || disabledAddress}
                lang={lang}
                noOptionsMessage={accountForm.addressCity.placeholder}
                content={accountForm.addressCity}
                value={state.addressFactualCity}
                optionValue={{
                  value: state.addressFactualCity,
                  label: state.addressFactualCity
                }}
                options={cityFactualList}
                valid={
                  state.addressFactualCity.length > 0
                    ? smartValidateString(state.addressFactualCity) &&
                    !errors.addressFactualCity
                    : !errors.addressFactualCity
                }
                onChange={addressFactualCity => {
                  console.log('addressFactualCity', addressFactualCity)
                  setValue('addressFactualCity', addressFactualCity, { shouldDirty: true })
                }}
                onCreateOption={(addressFactualCity: any) => {
                  console.log('addressFactualCity', addressFactualCity)
                  setValue('addressFactualCity', addressFactualCity, { shouldDirty: true })
                }}
                prefix={
                  state.addressFactualCountry === '398' && ['Алматинская область', 'Алматы облысы', 'Almaty'].includes(state.addressFactualRegion)
                    ? 'FactualAddressCity'
                    : state.addressFactualCountry === '643' && ['Москва и Московская обл.', 'Мәскеу және Мәскеу облысы', 'Moscow and Moscow region', 'Санкт-Петербург и область', 'Санкт-Петербург және облыс', 'St.Petersburg and Leningrad region'].includes(state.addressFactualRegion)
                      ? 'FactualAddressCityRf'
                      : undefined
                }
                isSearchable={true}
                creatable={true}
              />
            ) : (
              <TextInput
                testName="addressFactualCity"
                testErrorName="error-addressFactualCity"
                disabled={!state.agreed || disabledAddress}
                lang={lang}
                content={accountForm.addressCity}
                onChange={addressFactualCity => {
                  setValue('addressFactualCity', addressFactualCity, { shouldDirty: true })
                }}
                valid={
                  state.addressFactualCity.length > 0
                    ? smartValidateString(state.addressFactualCity) &&
                    !errors.addressFactualCity
                    : !errors.addressFactualCity
                }
                value={state.addressFactualCity}
                required
              />
            )}
          </div>
          <div className="col-4 col-xs-12">
            {/* FACTUAL ADDRESS STREET */}
            <TextInput
              testName="addressFactualStreet"
              testErrorName="error-addressFactualStreet"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressStreet}
              onChange={addressFactualStreet => {
                setValue('addressFactualStreet', addressFactualStreet, { shouldDirty: true })
              }}
              valid={
                state.addressFactualStreet.length > 0
                  ? smartValidateString(state.addressFactualStreet) &&
                  !errors.addressFactualStreet
                  : !errors.addressFactualStreet
              }
              value={state.addressFactualStreet}
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* FACTUAL ADDRESS BUILDING */}
            <TextInput
              testName="addressFactualBuilding"
              testErrorName="error-addressFactualBuilding"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressBuilding}
              onChange={addressFactualBuilding => {
                setValue('addressFactualBuilding', addressFactualBuilding, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualBuilding, 300) &&
                !errors.addressFactualBuilding
              }
              value={state.addressFactualBuilding}
              required
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* FACTUAL ADDRESS BUILDING BLOCK */}
            <TextInput
              testName="addressFactualBuildingBlock"
              testErrorName="error-addressFactualBuildingBlock"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressBuildingBlock}
              onChange={addressFactualBuildingBlock => {
                setValue('addressFactualBuildingBlock', addressFactualBuildingBlock, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualBuildingBlock, 300) &&
                !errors.addressFactualBuildingBlock
              }
              value={state.addressFactualBuildingBlock}
              required
            />
          </div>
          <div className="col-1 col-xs-12">
            {/* FACTUAL ADDRESS APARTMENTS */}
            <TextInput
              testName="addressFactualApartments"
              testErrorName="error-addressFactualApartments"
              disabled={!state.agreed || disabledAddress}
              lang={lang}
              content={accountForm.addressApartments}
              onChange={addressFactualApartments => {
                setValue('addressFactualApartments', addressFactualApartments, { shouldDirty: true })
              }}
              valid={
                validateString(state.addressFactualApartments, 50) &&
                !errors.addressFactualApartments
              }
              required
              value={state.addressFactualApartments}
            />
          </div>
        </div>
      </div>
    </>
  )
}
